<template>
    <div class="page" v-if="$store.state.editor.version" :class="{ 'study-locked': $store.state.editor.version.processStatus == 2 || $store.state.editor.version.processStatus == 3 }">
        <div class="page-menu g3">            
            <SideMenu :menu="sideMenu" ref="sideMenu" />
        </div>
        <div  class="loading" v-if="isPageLoading">            
            <div class="dot-pulse"  v-if="isPageLoading"></div>
        </div>
        
        <div class="page-content g9" :class="{'editor-loading' : isPageLoading}">
            
            <header>
                <template v-if="showAnonymousMenu">
                    <h2 class="breadcrumb">{{ project.title }}</h2>
                </template>
                <template v-else>
                    <h2 class="breadcrumb"><router-link :to="project.url">{{ project.title }}</router-link> <router-link :to="program.url">{{ program.title }}</router-link> <span class="forecast-title" :title="forecast.title">{{ forecast.title }}</span></h2>
                    <button class="versions outline" v-on:click="onToggleVersions($event, true)">Latest</button>
                </template>
            </header>

            <section>
                <div class="headlines">
                    <h2><span>{{ routeSection+1 }}.</span> {{ getHeadlines(routeSection).headline }}</h2>
                    <h4>{{ getHeadlines(routeSection).subHeadline }}</h4>
                    <div class="collapse" v-on:click="onToggleCollapse()" :class="isCollapsed ? '' : 'active'">
                        <label>
                            <span>Collapse all fields</span>
                            <div class="check">
                                <div></div>
                            </div>
                        </label>
                    </div>
                </div>               
                <form ref="studyForm" autocomplete="disabled">
                    <template v-if="editorType === 'study'">
                        <StudyEditor
                                :formValue="$store.state.editor.version.data"
                                @setSideMenu="setSideMenu"
                                @setHeadlines="setHeadlines"
                        ></StudyEditor>
                    </template>
                    <template v-else-if="editorType === 'aggregation'">
                        <AggregatorEditor
                                :formValue="$store.state.editor.version.data"
                                @setSideMenu="setSideMenu"
                                @setHeadlines="setHeadlines"
                                :is-page-loaded="isPageLoaded"
                        ></AggregatorEditor>
                    </template>
                </form>
                <div class="bottom-menu">
                    <button v-if="this.routeSection > 0" v-on:click="goToPage(routeSection-1)" class="left outline">{{ this.routeSection }} {{ this.getHeadlines(this.routeSection-1).headline }}</button>
                    <button v-if="this.routeSection < sideMenu.length-1" v-on:click="goToPage(routeSection+1)" class="right outline">{{ this.routeSection+2 }} {{ this.getHeadlines(this.routeSection+1).headline }}</button>
                </div>
            </section>

            <div class="versions modal show" v-if="openSideMenu" v-on:click="onToggleVersions($event, false)">
                <div class="close"></div>
                <menu>
                    <h2>Versions</h2>
                    <ul>
                        <li v-for="(item, index) in forecast.forecastVersions"
                            :class="(item.forecastVersionId === $route.params.versionId || index == 0 && $route.params.versionId == null) ? 'active' : ''">
                            <div :class="'status' + item.processStatus" class="state"></div>
                            <router-link :to="'/' + editorType + '/' + $route.params.id + '/0/0/' + item.forecastVersionId">
                                <template v-if="item.title">
                                    <h5>{{ item.title }}</h5>
                                </template>
                                <template v-else-if="index == 0">
                                    <h5>Latest version</h5>
                                </template>
                                <template v-else>
                                    <h5>Previous version</h5>
                                </template>
                            </router-link>

                            <small>{{ $moment.utc(item.updateDate).format("DD MMM YYYY HH:mm:ss") }}</small>
                            <div class="version-description">
                                <span class="description">{{item.description}}</span>
                            </div>
                            <div class="version-options">
                                <div class="rename">
                                    <span v-on:click="openRenameModal(item)">Rename</span>
                                </div>
                                <template v-if="allowMakeTemplate(item)">
                                    <div class="make-template">
                                        <span v-on:click="openTemplateModal(item)">To template</span>
                                    </div>
                                </template>
                                <template v-if="allowDelete()">
                                    <div class="delete">
                                        <span v-on:click="deleteVersion(item)">Delete</span>
                                    </div>
                                </template>
                            </div>
                        </li>
                    </ul>
                </menu>

                <Modal title="Rename version" ref="modalRename">
                    <form v-on:submit="onRenameSubmit">
                        <div class="input" type="text">
                            <label>
                                <span>Name</span>
                                <input type="text" placeholder="Version name" ref="renameVersionInput" v-model="renameVersion.title" />
                            </label>
                        </div>

                        <button class="right edit" v-bind:class="{ 'loading': renameVersion.isLoading }">Rename</button>
                    </form>
                </Modal>


                <Modal title="Create template" ref="modalCreateTemplate">
                    <form v-on:submit="onCreateTemplateSubmit">
                        <div class="input" type="text">
                            <label>
                                <span>Name</span>
                                <input type="text" placeholder="Template name" ref="createTemplateInput" v-model="createTemplate.title" />
                            </label>
                        </div>

                        <button class="right edit" v-bind:class="{ 'loading': createTemplate.isLoading }">Create</button>
                    </form>
                </Modal>

            </div>

            <Modal title="Input error" ref="modalValidator" v-if="$store.state.editor.validator.hasError || $store.state.editor.validator.hasWarning">

                <div class="validation-error" v-if="$store.state.editor.validator.hasError">
                    <h3>Errors</h3>
                    <div v-for="item in $store.state.editor.validator.errors">
                        <h4>{{ item.text }}</h4>
                        <ul>
                            <li v-for="error in item.errors">
                                {{ error.text }}
                                <HelperBox v-if="error.helper" :text="error.helper"></HelperBox>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="warning-error" v-if="$store.state.editor.validator.hasWarning">
                    <h3>Warnings</h3>
                    <div v-for="item in $store.state.editor.validator.warnings">
                        <h4>{{ item.text }}</h4>
                        <ul>
                            <li v-for="warning in item.warnings">
                                {{ warning.text }}
                                <HelperBox v-if="warning.helper" :text="warning.helper"></HelperBox>
                            </li>
                        </ul>
                    </div>
                </div>
            </Modal>
        </div>
    </div>
</template>

<script>
    import SideMenu from "../Components/SideMenuStudyEditor.vue"
    import StudyEditor from "../Components/Editor/StudyEditor.vue"
    import AggregatorEditor from "../Components/Editor/AggregatorEditor.vue"
    import Modal from "../Components/Modal.vue"
    import HelperBox from "../Components/HelperBox.vue"
import { isUndefined } from "util";

    export default {
        name: "Editor",
        components: {
            SideMenu,
            StudyEditor,
            AggregatorEditor,
            Modal,
            HelperBox
        },
        data: () => ({            
            routeSection: 0,
            routeLevel: 0,
            openSideMenu: false,
            isPageLoading: true,
            isPageLoaded: false,
            renameVersion: {
                title: null,
                isLoading: false,
                studyVersion: null
            },
            createTemplate: {
                title: null,
                isLoading: false,
                studyVersion: null
            },
            sideMenu: [],
            headlines: []
        }),
        computed:{
            editorType(){
                if(this.$route.name.indexOf("Aggregator") > -1)
                    return "aggregation";

                return "study";
            },
            program(){
                return this.$store.getters.program(this.forecast.programId)
            },
            project(){
                return this.$store.getters.project(this.program.projectId)
            },
            forecast(){
				if (this.editorType === "study") {
					//debugger;
                    return this.$store.getters.study(this.$route.params.id);
				}

                let program = this.$store.getters.program(this.$route.params.id);
                return {
                    programId: this.$route.params.id,
                    title: "Aggregation",
                    forecastVersions: program.forecastVersions
                }
            },
            isCollapsed:{
                get: function(){
                    return this.$store.state.isStudyEditorCollapsed;
                },
                set: function(isCollapsed){
                    this.$store.state.isStudyEditorCollapsed = isCollapsed;
                }
            },
            showAnonymousMenu(){
                return this.$store.state.member.isAnonymous;
            }
        },
        mounted(){
            this.isPageLoading = true;
            this.isPageLoaded = false;

            // Do special loading of editor if Anonymous
            if(this.showAnonymousMenu)
            {
				
                this.$store.dispatch("getAnonymousProject", this.$route.params.versionId).then(() => {
                    this.$store.state.editor.version = this.$store.getters.currentVersion();
                    document.title = this.forecast.title + " › " + this.program.title + " › " + this.project.title;
                    this.initPage();
                });
				
                return;
            }
            //debugger;
            // Load editor
            this.$store.state.editor.version = this.$store.getters.currentVersion();
            this.routeSection = parseInt(this.$route.params.section);
            this.routeLevel = parseInt(this.$route.params.level);

            // Set title
            document.title = this.forecast.title + " › " + this.program.title + " › " + this.project.title;

            // Check if study version data is loaded
            if(this.isEmptyObject(this.$store.state.editor.version.data))
            {                
                this.$store.dispatch("getForecastVersion", this.$store.state.editor.version.forecastVersionId).then(() => {                    
                    this.initPage();                    
                }).catch((res) => {                    
                    this.$store.state.editor.validator.hasError = true;
                    this.$store.state.editor.validator.show = true;
                  let errorData = "";
                  if (!isUndefined(res.response) && !isUndefined(res.response.data))
                  {
                        errorData = res.response.data;
                  }
                    this.$store.state.error.data = errorData;
                    this.isPageLoading = false;
                    this.isPageLoaded = true;
                });
                    
            }
            else
            {
                this.initPage();
            }
        },
        beforeDestroy(){
            this.$store.state.editor.version = null;
            this.$store.state.editor.validator.hasError = false;
            this.$store.state.editor.validator.hasWarning = false;
            this.$store.state.editor.validator.errors = [];
        },
        methods: {
            setSideMenu(menu){
                this.sideMenu = menu;
            },
            setHeadlines(headlines){
                this.headlines = headlines;
            },
            allowDelete() {
                return this.$store.state.member.isSuperAdmin;
            },
            allowMakeTemplate(item) {

                //only completed versions are allowe to become templates
                if (item.processStatus != 2 && item.processStatus != 3)
                    return false;

                return this.$store.state.member.isSuperAdmin;
            },
            initPage() {
                // Scroll down to selected section
				//debugger;
                this.$nextTick(() => {
                    this.setCollapse();
                    this.scrollPage();
                    this.isPageLoading = false;
                    this.isPageLoaded = true;
                })
				//debugger;
            },
            scrollPage() {				
                if (this.routeLevel > 0 && !isUndefined(this.$refs.studyForm)) {
                    let aList = this.$refs.studyForm.querySelectorAll(".formBuilder header a");

                    for (const a of aList) {
                        let linkId = "link" + (this.routeSection + 1) + "." + (this.routeLevel + 1);
                        if (a.dataset.id != linkId) {
                            continue;
                        }
                        /*
                        console.log(linkId)
                        console.log(this.routeLevel);
                        */
                        a.scrollIntoView(true);
                        
                       
                        /*
                         * we are testing options here: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
                         * not working
                        else {
                            //a.scrollIntoView(true);
                            //a.scrollIntoView({ behavior: "smooth", block: "nearest"});
                        }
                        */
                        break;
                    }
                } else {
                    window.scrollTo(0, 0);
                }
            },
            getHeadlines(section){
                let returns = {
                    headline: "",
                    subHeadline: ""
                };

                if(this.headlines && this.headlines.length > 0){
                    returns = this.headlines[section];
                }

                return returns;
            },
            goToPage(section, level = 0){
                this.isPageLoading = true;
                history.replaceState({}, null, this.createEditorMenuUrl(this.editorType, section, level));
                this.routeSection = section;
                this.routeLevel = level;
                this.initPage();
                this.$refs.sideMenu.openMenu(section);
            },
            onToggleCollapse(){
                this.isCollapsed = !this.isCollapsed;
                this.setCollapse();
            },
            setCollapse(){
				if (!this.NullOrUndefined(this.$refs.studyForm)) {
                this.$refs.studyForm.querySelectorAll(".accord").forEach((elm) =>{
                    if(this.isCollapsed)
                        elm.classList.remove("closed");
                    else
                        elm.classList.add("closed")
                })}
            },
            onToggleVersions(e, open){
                if(e.target.classList.contains("versions") || e.target.classList.contains("close") || e.target.classList.contains("outline"))
                    this.openSideMenu = open;
            },
            deleteVersion(item) {
                if (confirm("Are you sure you want to delete version: " + item.title)) {
                    
                    if (this.$store.state.editor.version.forecastVersionId == item.forecastVersionId) {
                        if (confirm("You are about to delete the version you are currently editing. You will be redirected back. Are you sure?")) {
                            this.$store.dispatch("deleteForecastVersion", item).then((res) => {
                                this.$router.push(this.program.url);
                            }).catch((res) => {
                            });
                        }
                    }
                    else {
                        this.$store.dispatch("deleteForecastVersion", item).then((res) => {
                            var deletedIndex = this.forecast.forecastVersions.findIndex(x => x.forecastVersionId == item.forecastVersionId);
                            if (deletedIndex > -1) {                                
                                this.forecast.forecastVersions.splice(deletedIndex, 1);
                            }                            
                        }).catch((res) => {

                        });
                    }
                    
                }
            },
            openRenameModal(item){
                this.renameVersion.title = item.title;
                this.renameVersion.studyVersion = item;
                this.$refs.modalRename.show();
                this.$nextTick(() => this.$refs.renameVersionInput.select());
            },
            openTemplateModal(item) {
                this.createTemplate.title = item.title;                
                this.createTemplate.studyVersion = item;         
                this.$refs.modalCreateTemplate.show();
                this.$nextTick(() => this.$refs.createTemplateInput.select());
            },
            onCreateTemplateSubmit(e) {
                e.preventDefault();

                if (this.createTemplate.title.length < 1)
                    return false;

                this.createTemplate.isLoading = true;

                let data = {
                    forecastVersionId: this.createTemplate.studyVersion.forecastVersionId,
                    title: this.createTemplate.title
                };

                this.$store.dispatch("createTemplateFromVersion", data).then((res) => {
                    this.createTemplate.isLoading = false;
                    this.$refs.modalCreateTemplate.hide();
                }).catch((res) => {
                    this.createTemplate.isLoading = false;
                    this.$refs.modalCreateTemplate.hide();
                });
                
            }
            ,
            onRenameSubmit(e) {
                e.preventDefault();

                if(this.renameVersion.title.length < 1)
                    return false;

                this.renameVersion.isLoading = true;

                let data = {
                    forecastVersionId: this.renameVersion.studyVersion.forecastVersionId,
                    title: this.renameVersion.title
                };

                this.renameVersion.studyVersion.title = this.renameVersion.title;

                this.$store.dispatch("renameForecastVersion", data).then((res) => {
                    this.renameVersion.isLoading = false;
                    this.$refs.modalRename.hide();
                }).catch((res) => {
                    this.renameVersion.isLoading = false;
                    this.$refs.modalRename.hide();
                });
            }
        }
    }
</script>
